import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Button, Container, Drawer, IconButton, List, ListItemButton, ListItemText } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { detectPlatform, shiftwave, trackDownload } from '../../utils/downloads';
import './Header.scss';

const navItems = [
    { label: 'Features', to: { pathname: '/', hash: '#features' } },
    { label: 'Schedules', to: { pathname: '/', hash: '#schedules' } },
    { label: 'Pricing', to: { pathname: '/', hash: '#pricing' } },
    { label: 'CopApp!', to: { pathname: '/', hash: '#copapp' } },
    { label: 'FAQ', to: { pathname: '/', hash: '#faq' } },
    { label: 'About', to: { pathname: '/about' } },
    { label: 'Contact', to: { pathname: '/contact' } },
];

const Header: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => setOpen(false), [location]);

    // On a phone the CTA goes straight to that phone's store; on desktop it
    // scrolls to the download section with the QR code.
    const platform = detectPlatform();
    const ctaHref = platform === 'ios' ? shiftwave.appStoreLink : platform === 'android' ? shiftwave.googlePlayLink : undefined;

    const cta = ctaHref ? (
        <Button
            variant="contained"
            href={ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackDownload('shiftwave', platform === 'ios' ? 'appstore' : 'googleplay', 'header')}
        >
            Get ShiftWave
        </Button>
    ) : (
        <Button variant="contained" component={RouterLink} to={{ pathname: '/', hash: '#download' }}>
            Get ShiftWave
        </Button>
    );

    return (
        <header className={`site-header${scrolled ? ' site-header--scrolled' : ''}`}>
            <Container maxWidth="lg" className="site-header__inner">
                <RouterLink to="/" className="site-header__brand" aria-label="MyProjectX home">
                    <img src="/images/planet-logo-dark-128.png" alt="" width={32} height={32} />
                    <span>MyProjectX</span>
                </RouterLink>

                <nav className="site-header__nav" aria-label="Main">
                    {navItems.map((item) => (
                        <RouterLink key={item.label} to={item.to} className="site-header__link">
                            {item.label}
                        </RouterLink>
                    ))}
                </nav>

                <Box className="site-header__actions">
                    {cta}
                    <IconButton
                        className="site-header__menu"
                        aria-label="Open menu"
                        onClick={() => setOpen(true)}
                        edge="end"
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>
            </Container>

            <Drawer
                anchor="right"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{ className: 'site-header__drawer' }}
            >
                <Box className="site-header__drawer-top">
                    <span>Menu</span>
                    <IconButton aria-label="Close menu" onClick={() => setOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <List>
                    {navItems.map((item) => (
                        <ListItemButton key={item.label} component={RouterLink} to={item.to}>
                            <ListItemText primary={item.label} />
                        </ListItemButton>
                    ))}
                </List>
            </Drawer>
        </header>
    );
};

export default Header;
